import { Tooltip } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { TrainingEvalRunView, TrainingSessionPoliciesView } from "@/api/trainingEval";
import { CompactCount } from "@/components/table/CompactCount";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { EvalRunAnnotationEditor } from "./EvalRunAnnotationEditor";
import {
  EvalRunDetailDatasetValue,
  EvalRunDetailInfo,
  EvalRunDetailInfoList,
  EvalRunDetailSection,
} from "./EvalRunDetailElements";
import {
  checkpointDisplay,
  evaluatedArtifact,
  evaluatedCheckpointFromParams,
} from "./evalRunPolicyModel";
import { formatDetailDate } from "./evalRunResultModel";
import type { EvalRunDetailController } from "./useEvalRunDetailController";
import styles from "./EvalRunDetailView.module.css";

export function EvalRunPolicyInfoSection({
  summary,
  detail,
  policies,
  loadError,
  onRetry,
}: {
  summary: EvalRunDetailController["summary"];
  detail: TrainingEvalRunView | null;
  policies: TrainingSessionPoliciesView | null;
  loadError: boolean;
  onRetry: () => void;
}): ReactNode {
  const checkpoints = policies?.policies ?? [];
  const artifact = evaluatedArtifact(detail, policies);
  return (
    <EvalRunDetailSection title="Policy 정보">
      <EvalRunDetailInfoList>
        <EvalRunDetailInfo label="branch" value={summary.branch ?? "-"} copyText={summary.branch} />
        <EvalRunDetailInfo
          label="git hash"
          value={summary.git_short_hash ?? "-"}
          copyText={summary.git_short_hash}
        />
        <EvalRunDetailInfo
          label="dataset"
          value={
            <EvalRunDetailDatasetValue
              upstreamLabel={summary.upstream_dataset_label}
              registryRef={summary.dataset_registry_ref}
            />
          }
        />
        <EvalRunDetailInfo label="experiment id" value={policies?.experiment_id ?? "-"} />
        <EvalRunDetailInfo
          label="Checkpoint"
          value={checkpoints.length > 0 ? checkpoints.length : "-"}
        />
        <EvalRunDetailInfo
          label="평가한 Checkpoint"
          value={<EvaluatedCheckpointValue detail={detail} />}
        />
        <EvalRunDetailInfo label="총 Epoch" value={summary.source_session_total_epochs ?? "-"} />
        <EvalRunDetailInfo
          label="총 Step"
          value={
            summary.source_session_total_steps == null ? (
              "-"
            ) : (
              <CompactCount value={summary.source_session_total_steps} />
            )
          }
        />
        <EvalRunDetailInfo label="평가한 Artifact" value={artifact ?? "-"} copyText={artifact} />
      </EvalRunDetailInfoList>
      {loadError ? (
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
          Policy 정보를 확인할 수 없습니다.
        </EmptyState>
      ) : null}
    </EvalRunDetailSection>
  );
}

function EvaluatedCheckpointValue({ detail }: { detail: TrainingEvalRunView | null }): ReactNode {
  const tag = evaluatedCheckpointFromParams(detail?.params ?? null);
  if (tag === null) return "—";
  const { text, title } = checkpointDisplay(tag);
  return title === null ? text : <Tooltip content={title}>{text}</Tooltip>;
}

export function EvalRunInfoSection({
  controller,
  detail,
  scenarioName,
}: {
  controller: EvalRunDetailController;
  detail: TrainingEvalRunView | null;
  scenarioName: string | null;
}): ReactNode {
  const { summary, detailState } = controller;
  return (
    <EvalRunDetailSection title="실행 정보">
      <EvalRunDetailInfoList>
        <EvalRunDetailInfo label="run id" value={summary.id} copyText={summary.id} />
        <EvalRunDetailInfo
          label="session id"
          value={summary.source_session_id ?? "-"}
          copyText={summary.source_session_id}
        />
        <EvalRunDetailInfo
          label="mlflow run id"
          value={detail?.mlflow_run_id ?? "-"}
          copyText={detail?.mlflow_run_id}
        />
        <EvalRunDetailInfo label="scenario" value={scenarioName ?? "-"} copyText={scenarioName} />
        <EvalRunDetailInfo label="종류" value={detail?.kind ?? "-"} />
        <EvalRunDetailInfo label="gpu share" value={detail?.gpu_share_count ?? "-"} />
        <EvalRunDetailInfo label="원본 Evaluation 실행" value={detail?.source_eval_run_id ?? "-"} />
        <EvalRunDetailInfo label="branch" value={summary.branch ?? "-"} />
        <EvalRunDetailInfo
          label="시작"
          value={formatDetailDate(summary.started_at ?? summary.created_at)}
        />
        <EvalRunDetailInfo
          label="종료"
          value={formatDetailDate(detail?.finished_at ?? summary.finished_at)}
        />
      </EvalRunDetailInfoList>
      {/* 상세를 받지 못하면 위 목록의 여러 값이 `-` 다. 값이 없는 실행과 구분되도록 실패를 알린다. */}
      {detailState.status === "error" ? (
        <EmptyState
          size="sm"
          tone="error"
          action={{ label: "다시 시도", onClick: controller.retry }}
        >
          실행 상세를 불러오지 못했습니다: {detailState.message.slice(0, 180)}
        </EmptyState>
      ) : null}
      <div className={styles.chipGroup}>
        <span className={styles.muted}>memo</span>
        <EvalRunAnnotationEditor evalRunId={summary.id} />
      </div>
    </EvalRunDetailSection>
  );
}

export function EvalRunMlflowLink({
  runId,
  url,
}: {
  runId: string | null;
  url: string | null;
}): ReactNode {
  if (runId === null || url === null) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.mlflow}
      aria-label={`MLflow run ${runId} 을 새 탭에서 엽니다`}
    >
      MLflow ↗
    </a>
  );
}
